import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { QarepaBaseSubsDirective } from '@qarepa/common';
import { AssessmentResultsCommentsRegisterComponent } from '../dialogs/assessment-results-comments-register/assessment-results-comments-register.component';
import { AssessmentEmployeesResults } from '@qaroni-app/assessment/types/assessment-employees-results';
import { AssessmentResultsAddCommentDialogData } from '@qaroni-app/assessment/types/assessment-results-add-feedback-dialog-data';
import { AssessmentResultsComment } from '@qaroni-app/assessment/types/assessment-results-comment';
import { ImagesConfig } from '@qaroni-core/config/app/images.config';
import { ConfirmationDialogData } from '@qaroni-core/types/dialogs/confirmation-dialog-data';
import { QaroniDialogConfig } from '@qaroni-core/types/dialogs/qaroni-dialog-config';
import { ConfirmationDialogComponent } from '../dialogs/confirmation-dialog/confirmation-dialog.component';

@Component({
  selector: 'qaroni-result-comment-item',
  templateUrl: './result-comment-item.component.html',
  styleUrl: './result-comment-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ResultCommentItemComponent extends QarepaBaseSubsDirective {
  readonly comment = input<AssessmentResultsComment | null>(null);
  readonly employeeID = input<number>(0);
  readonly assessmentID = input<number>(0);
  readonly evaluationID = input<number>(0);
  readonly employees = input<AssessmentEmployeesResults[]>([]);
  readonly showEmployee = input<boolean>(false);
  readonly showEmployeeList = input<boolean>(true);
  readonly showActions = input<boolean>(true);

  readonly updateComment = output<AssessmentResultsComment>();
  readonly deleteComment = output<AssessmentResultsComment>();

  private dialog = inject(MatDialog);

  public avatar = ImagesConfig.maleAvatar;

  get isMyComment() {
    const comment = this.comment();
    return comment?.owner === true || comment?.employeeId === this.employeeID();
  }

  get date() {
    const comment = this.comment();
    return comment?.creationDate ?? comment?.date;
  }

  public onEditComment() {
    const comment = this.comment();
    const assessmentID = this.assessmentID();
    const assessmentCreateData: AssessmentResultsAddCommentDialogData = {
      assessmentID: assessmentID ? assessmentID : this.evaluationID(),
      comment: comment,
      employeeID: comment?.employee_evaluation
        ? comment.employee_evaluation.employeeId
        : comment?.employeeId,
      employees: this.employees(),
      showEmployeesList: this.showEmployeeList(),
    };

    const dialogConfig =
      new QaroniDialogConfig<AssessmentResultsAddCommentDialogData>();
    dialogConfig.data = assessmentCreateData;
    dialogConfig.panelClass = 'qaroni-style-full-close-dialog';
    dialogConfig.width = '450px';
    const dialog = this.dialog.open<
      AssessmentResultsCommentsRegisterComponent,
      AssessmentResultsAddCommentDialogData,
      AssessmentResultsComment
    >(AssessmentResultsCommentsRegisterComponent, dialogConfig);
    this.subs.add(dialog.afterClosed().subscribe(this.getDialogComment));
  }

  public onDeleteComment() {
    const confirmationDialogData: ConfirmationDialogData = {
      title: $localize`Eliminar Retroalimentación`,
      message: $localize`¿Estás seguro que deseas eliminar esta retroalimentación? Esta acción no se podrá deshacer.`,
      cancelColorButton: `light-blue`,
      confirmColorButton: `danger`,
      confirmMatIcon: `delete`,
      confirmText: $localize`Eliminar`,
    };
    const dialogConfig = new QaroniDialogConfig<ConfirmationDialogData>();
    dialogConfig.data = confirmationDialogData;
    const dialog = this.dialog.open<
      ConfirmationDialogComponent,
      ConfirmationDialogData,
      boolean
    >(ConfirmationDialogComponent, dialogConfig);
    this.subs.add(dialog.afterClosed().subscribe(this.getData));
  }

  private getDialogComment = (commentData: AssessmentResultsComment) => {
    if (!commentData || commentData.message.trim().length === 0) {
      return;
    }

    const comment: AssessmentResultsComment = {
      ...this.comment(),
      message: commentData.message,
    };
    this.updateComment.emit(comment);
  };

  private getData = (response: boolean): void => {
    if (response) {
      this.deleteComment.emit(this.comment());
    }
  };
}
